/** Default color and stroke values for drawing shapes */
export const DRAWING_DEFAULTS = {
  /** White stroke (ARGB integer) */
  STROKE_COLOR: 0xFFFFFFFF,
  STROKE_WEIGHT: 4.0,
  /** Semi-transparent blue fill (ARGB integer) */
  FILL_COLOR: 0x9700BFFF,
  /** Semi-transparent red fill for geofences (ARGB integer) */
  GEOFENCE_FILL: 0x64FF0000,
} as const;

/**
 * Convert a TAK ARGB integer to a CSS hex color string.
 * Extracts the RGB channels, ignoring alpha.
 */
export function argbIntToHex(argb: number): string {
  const r = (argb >>> 16) & 0xFF;
  const g = (argb >>> 8) & 0xFF;
  const b = argb & 0xFF;
  return '#' + toHexPair(r) + toHexPair(g) + toHexPair(b);
}

/**
 * Convert a CSS hex color string and opacity (0–1) to a TAK ARGB integer.
 * Accepts '#rrggbb' or '#rgb' format.
 */
export function hexToArgbInt(hex: string, opacity: number): number {
  const cleaned = hex.startsWith('#') ? hex.slice(1) : hex;
  const expanded = expandShortHex(cleaned);

  const r = parseHexPair(expanded, 0);
  const g = parseHexPair(expanded, 2);
  const b = parseHexPair(expanded, 4);
  const a = Math.round(opacity * 255);

  return ((a << 24) | (r << 16) | (g << 8) | b) >>> 0;
}

/**
 * Extract the alpha channel from an ARGB integer as a 0–1 float.
 */
export function argbAlpha(argb: number): number {
  return ((argb >>> 24) & 0xFF) / 255;
}

/**
 * Convert a TAK ARGB integer to an 8-digit CSS hex color (#rrggbbaa) that
 * carries the alpha channel. Used where a renderer has no separate fill-opacity
 * knob (e.g. the 3D Polygon3DElement), so a transparent alpha yields no fill.
 */
export function argbIntToHex8(argb: number): string {
  const a = (argb >>> 24) & 0xFF;
  const r = (argb >>> 16) & 0xFF;
  const g = (argb >>> 8) & 0xFF;
  const b = argb & 0xFF;
  return '#' + toHexPair(r) + toHexPair(g) + toHexPair(b) + toHexPair(a);
}

/** Convert a single byte to a two-character hex string */
function toHexPair(byte: number): string {
  const HEX_CHARS = '0123456789abcdef';
  return HEX_CHARS[byte >>> 4]! + HEX_CHARS[byte & 0xF]!;
}

/** Parse two hex characters from a string at the given offset */
function parseHexPair(str: string, offset: number): number {
  return parseInt(str.slice(offset, offset + 2), 16);
}

/** Expand 3-char shorthand hex to 6-char */
function expandShortHex(hex: string): string {
  if (hex.length === 3) {
    return hex[0]! + hex[0]! + hex[1]! + hex[1]! + hex[2]! + hex[2]!;
  }
  return hex;
}
